"use client"

import * as React from "react"
import { useState } from "react"

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "@kit/ui/drawer"
import { Trans } from "@kit/ui/trans"
import { ContentCatalog } from "./content-catalog"
import { Tabs, TabsList, TabsTrigger } from "@kit/ui/tabs"
import { If } from "@kit/ui/if"
import { CircleX, Link } from "lucide-react"
import { toast } from "sonner"

export type CatalogContentType = "products" | "links"

export function CatalogDrawer({
  children,
  accountId,
  clientId,
  openCatalog,
}: React.PropsWithChildren<{
  accountId: string,
  clientId: string,
  openCatalog?: CatalogContentType
}>) {
  const [selectedTab, setSelectedTab] = useState<CatalogContentType>(openCatalog || "products");
  const [isOpen, setOpen] = useState<boolean>(!!openCatalog);

  React.useEffect(() => {
    setSelectedTab(openCatalog || "products");
    setOpen(!!openCatalog);
  }, [openCatalog])

  React.useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams();
      params.set('o', selectedTab);
      window.history.replaceState({}, '', `?${params.toString()}`);
    } else {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isOpen, selectedTab]);
  
  const copyToClipboard = async () => {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    toast.success('Catalog URL copied to clipboard');
  };

  return (
    <Drawer dismissible={false} open={isOpen} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        {children}
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerOverlay className="fixed inset-0 bg-black/40" />
        <DrawerContent className="flex flex-col rounded-t-[10px] mt-24 fixed bottom-0 left-0 right-0 outline-none h-svh z-50" hideHandle>
          <DrawerHeader className="container max-w-4xl mt-6">
            <div className="justify-between flex flex-row">
              <div className="flex flex-col space-y-2 items-start">
                <DrawerTitle>
                  <div className="flex flex-row items-center">
                    <Trans i18nKey={'teams:productCatalogTitle'} />
                    <Link className="ml-2" size={20} onClick={() => copyToClipboard()}/>
                  </div>
                </DrawerTitle>
                <DrawerDescription>
                  <Trans i18nKey={'teams:productCatalogDescription'} />
                </DrawerDescription>
              </div>
              <CircleX size={32} onClick={() => setOpen(false)}/>
            </div>
          </DrawerHeader>
          <div className="flex flex-row justify-center items-center space-x-2 pb-4 z-50">
            <Tabs defaultValue={selectedTab} className="self-center" orientation="vertical" onValueChange={(value) => setSelectedTab(value as CatalogContentType)}>
              <TabsList>
                <TabsTrigger value="products">Products</TabsTrigger>
                <TabsTrigger value="links">Links</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex-1 overflow-y-auto">
            <If condition={selectedTab === "products"}><ContentCatalog accountId={accountId} clientId={clientId} contentType="product" /></If>
            <If condition={selectedTab === "links"}><ContentCatalog accountId={accountId} clientId={clientId} contentType="link" /></If>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  )
}